import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {Workspace} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    ActivityCardType,
    type ActivityCardData,
    type ActivityTimelineBuildContext,
    type EventTypeActivityCardData,
    type LogCardData,
} from "~/modules/activityTimeline/types/activityTimeline.types";
import {
    ACTIVITY_TIMELINE_SYSTEM_CREATED_BY_DISPLAY_NAME,
} from "~/modules/activityTimeline/utils/activityTimelineCreatedBy";
import {
    buildOldNewContext,
    formatActivityColumnFieldValue,
    resolveActivityUserDisplayName,
    resolveActivityUserRoleLabel,
    resolveActivityUserShowroomName,
    resolveDispositionDisplayName,
    resolveEventStatusDisplayForActivityTimeline,
    resolveUtmSourceFromAdditionalColumnValues,
} from "~/modules/activityTimeline/utils/activityTimelineDisplayUtils";
import {
    resolveEventTemplateDisplayTitle,
    resolveEventTypeActivityContext,
    resolveEventTypeActivityRemarks,
} from "~/modules/activityTimeline/utils/resolveEventActivityContext";
import {
    resolveActivityLogCardIsInactiveFromEventContainerStatus,
} from "~/modules/activityTimeline/utils/resolveActivityLogCardIsInactive";
import {isDeprecatedSystemGeneratedCreatedUpdatedByUserId} from "~/utilities/deprecatedSystemGeneratedCreatedUpdatedByUserIds";

const DEFAULT_JOURNEY_LABEL = "J1";

function resolveCreatedBy(
    card: ActivityCardData,
    context: ActivityTimelineBuildContext,
): LogCardData["createdBy"] {
    const userId = card.createdBy.userId;
    if (card.createdBy.kind === "system" || userId === null) {
        return {kind: "system", displayName: ACTIVITY_TIMELINE_SYSTEM_CREATED_BY_DISPLAY_NAME};
    }
    if (isDeprecatedSystemGeneratedCreatedUpdatedByUserId(userId) === true) {
        return {kind: "system", displayName: ACTIVITY_TIMELINE_SYSTEM_CREATED_BY_DISPLAY_NAME};
    }
    const displayName = resolveActivityUserDisplayName(
        userId,
        context.users,
        context.userTemplates,
        context.platformUserDetailsByUserId,
    );
    const roleLabel = resolveActivityUserRoleLabel(userId, context.users, context.userTemplates);
    return {
        kind: "user",
        displayName,
        roleLabel: roleLabel === "" ? undefined : roleLabel,
    };
}

function fieldValueOptions(context: ActivityTimelineBuildContext, workspace: Workspace) {
    return {
        workspace: workspace,
        users: context.users,
        userTemplates: context.userTemplates,
        organizationalUnits: context.organizationalUnits,
        organizationalUnitTemplates: context.organizationalUnitTemplates,
        platformUserDetailsByUserId: context.platformUserDetailsByUserId,
        context: context,
    };
}

function formatFieldChangeValue(getAdditionalColumns: CapabilityUdoColumnResolver,
    value: unknown,
    columnId: string | undefined,
    context: ActivityTimelineBuildContext,
    workspace: Workspace,
    eventTemplateId?: Uuid,
): string {
    return formatActivityColumnFieldValue(getAdditionalColumns, value, columnId, {
        ...fieldValueOptions(context, workspace),
        eventTemplateId: eventTemplateId,
    });
}

function resolveJourneyUtmSource(context: ActivityTimelineBuildContext): string | undefined {
    const columnIds = context.workspaceSpecific?.journeyUtmSourceColumnIds ?? [];
    return resolveUtmSourceFromAdditionalColumnValues(context.eventContainer.additionalColumnValues, columnIds);
}

function resolveEventUtmSourceForCard(
    card: EventTypeActivityCardData,
    context: ActivityTimelineBuildContext,
): string | undefined {
    if (card.sourceRaw !== null && card.sourceRaw !== undefined && card.sourceRaw.trim() !== "") {
        return card.sourceRaw.trim();
    }
    const event = context.eventContainer.events.find((item) => item.id === card.eventId);
    if (event === undefined) {
        return undefined;
    }
    const columnIds = context.workspaceSpecific?.eventUtmSourceColumnIds ?? [];
    return resolveUtmSourceFromAdditionalColumnValues(event.additionalColumnValues, columnIds);
}

function resolveCardSource(
    card: ActivityCardData,
    context: ActivityTimelineBuildContext,
): string | undefined {
    if (card.cardType === ActivityCardType.eventType) {
        return resolveEventUtmSourceForCard(card, context) ?? resolveJourneyUtmSource(context);
    }
    return resolveJourneyUtmSource(context);
}

export function buildLogCardData(getAdditionalColumns: CapabilityUdoColumnResolver,
    card: ActivityCardData,
    context: ActivityTimelineBuildContext,
    workspace: Workspace,
): LogCardData {
    const createdBy = resolveCreatedBy(card, context);
    const source = resolveCardSource(card, context);
    const ou =
        createdBy.kind === "user" && card.createdBy.userId !== null
            ? resolveActivityUserShowroomName(
                  card.createdBy.userId,
                  workspace,
                  context.users,
                  context.organizationalUnits,
                  context.organizationalUnitTemplates,
              )
            : undefined;

    if (card.cardType === ActivityCardType.eventType) {
        const event = context.eventContainer.events.find((item) => item.id === card.eventId);
        const statusDisplay =
            (card.statusRaw !== null && card.statusRaw !== undefined && card.statusRaw.trim() !== ""
                ? card.statusRaw
                : undefined) ??
            (event !== undefined
                ? resolveEventStatusDisplayForActivityTimeline(getAdditionalColumns, card.eventTemplateId, event.additionalColumnValues as Record<string, unknown>, context)
                : undefined);
        const activityContext = resolveEventTypeActivityContext(getAdditionalColumns, card, context, statusDisplay);
        return {
            id: card.id,
            cardType: card.cardType,
            timestamp: card.timestamp,
            status: statusDisplay,
            entityAction: resolveEventTemplateDisplayTitle(context, card.eventTemplateId),
            context: activityContext.context,
            contextPrefix: activityContext.contextPrefix,
            contextOldValue: activityContext.contextOldValue,
            contextNewValue: activityContext.contextNewValue,
            remarks: resolveEventTypeActivityRemarks(card, context),
            createdBy,
            ou,
            source,
            journeyLabel: DEFAULT_JOURNEY_LABEL,
        };
    }

    if (card.cardType === ActivityCardType.dispositionChange) {
        const oldLabel = resolveDispositionDisplayName(getAdditionalColumns, card.oldDispositionId, context.eventContainerTemplate, context.userDefinedObjectOptionsByTemplateId);
        const newLabel = resolveDispositionDisplayName(getAdditionalColumns, card.newDispositionId, context.eventContainerTemplate, context.userDefinedObjectOptionsByTemplateId);
        const hasOld = oldLabel !== "-" && oldLabel !== "?";
        const hasNew = newLabel !== "-" && newLabel !== "?";
        const hasOldNew = hasOld && hasNew;
        return {
            id: card.id,
            cardType: card.cardType,
            timestamp: card.timestamp,
            entityAction: "Dispose",
            context: hasOldNew ? "" : hasNew ? newLabel : hasOld ? oldLabel : "Disposition changed",
            contextPrefix: hasOldNew ? "Disposition changed to " : undefined,
            contextOldValue: hasOldNew ? oldLabel : undefined,
            contextNewValue: hasOldNew ? newLabel : undefined,
            remarks: card.remarks ?? undefined,
            createdBy,
            ou,
            source,
            journeyLabel: DEFAULT_JOURNEY_LABEL,
        };
    }

    if (card.cardType === ActivityCardType.agentChange) {
        const valueOptions = fieldValueOptions(context, workspace);
        const oldDisplay = formatActivityColumnFieldValue(getAdditionalColumns, card.oldValue, String(card.columnId), valueOptions);
        const newDisplay = formatActivityColumnFieldValue(getAdditionalColumns, card.newValue, String(card.columnId), valueOptions);
        const hasOld = oldDisplay !== "-";
        const hasNew = newDisplay !== "-";
        const hasOldNew = hasOld && hasNew;
        return {
            id: card.id,
            cardType: card.cardType,
            timestamp: card.timestamp,
            entityAction: "Ownership Transferred",
            context: hasOldNew ? "" : `Assigned to ${newDisplay}`,
            contextOldValue: hasOldNew ? oldDisplay : undefined,
            contextNewValue: hasOldNew ? newDisplay : undefined,
            remarks: card.remarks ?? undefined,
            createdBy,
            ou,
            source,
            journeyLabel: DEFAULT_JOURNEY_LABEL,
        };
    }

    const valueOptions = fieldValueOptions(context, workspace);

    if (card.isDelete === true) {
        return {
            id: card.id,
            cardType: card.cardType,
            timestamp: card.timestamp,
            entityAction: "Detail Deleted",
            context: "Context",
            remarks: card.remarks ?? undefined,
            createdBy,
            ou,
            source,
            journeyLabel: DEFAULT_JOURNEY_LABEL,
        };
    }

    if (card.fieldChanges.length === 0) {
        return {
            id: card.id,
            cardType: card.cardType,
            timestamp: card.timestamp,
            entityAction: "Detail Edited",
            context: "Lead created",
            remarks: card.remarks ?? undefined,
            createdBy,
            ou,
            source,
            journeyLabel: DEFAULT_JOURNEY_LABEL,
        };
    }

    const contextFieldChanges = card.fieldChanges
        .map((change) => {
            const oldDisplay = formatFieldChangeValue(getAdditionalColumns, change.oldValue, change.fieldKey, context, workspace, card.eventTemplateId);
            const newDisplay = formatFieldChangeValue(getAdditionalColumns, change.newValue, change.fieldKey, context, workspace, card.eventTemplateId);
            return {
                fieldLabel: change.fieldLabel,
                oldDisplay: oldDisplay,
                newDisplay: newDisplay,
            };
        })
        .filter((line) => line.newDisplay !== "-" || line.oldDisplay !== "-");

    if (card.fieldLabel !== undefined && contextFieldChanges.length === 0) {
        const oldDisplay = formatFieldChangeValue(getAdditionalColumns, card.oldValue, undefined, context, workspace, card.eventTemplateId);
        const newDisplay = formatFieldChangeValue(getAdditionalColumns, card.newValue, undefined, context, workspace, card.eventTemplateId);
        const hasOldNew = oldDisplay !== "-" && newDisplay !== "-";
        return {
            id: card.id,
            cardType: card.cardType,
            timestamp: card.timestamp,
            entityAction: "Detail Edited",
            context: hasOldNew ? "" : `${card.fieldLabel}: ${buildOldNewContext(getAdditionalColumns, card.oldValue, card.newValue, valueOptions)}`,
            contextOldValue: hasOldNew ? oldDisplay : undefined,
            contextNewValue: hasOldNew ? newDisplay : undefined,
            contextFieldChanges: hasOldNew
                ? [{fieldLabel: card.fieldLabel, oldDisplay, newDisplay}]
                : undefined,
            remarks: card.remarks ?? undefined,
            createdBy,
            ou,
            source,
            journeyLabel: DEFAULT_JOURNEY_LABEL,
        };
    }

    return {
        id: card.id,
        cardType: card.cardType,
        timestamp: card.timestamp,
        entityAction: "Detail Edited",
        context: "",
        contextFieldChanges,
        remarks: card.remarks ?? undefined,
        createdBy,
        ou,
        source,
        journeyLabel: DEFAULT_JOURNEY_LABEL,
    };
}

export function logCardBuilder(getAdditionalColumns: CapabilityUdoColumnResolver,
    cards: Array<ActivityCardData>,
    context: ActivityTimelineBuildContext,
    workspace: Workspace,
): Array<LogCardData> {
    const isInactive = resolveActivityLogCardIsInactiveFromEventContainerStatus(context.eventContainer.status);
    return cards.map((card) => ({
        ...buildLogCardData(getAdditionalColumns, card, context, workspace),
        isInactive,
    }));
}
